import React, { ReactNode, useEffect } from 'react';
import { batch, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { bootstrapQuery } from '../../utils/apollo/globalConfig';
import {
  IBootstrapQueryQuery,
  IBootstrapQueryQueryVariables,
  UserSettingKind,
} from '../../utils/apollo/graphqlSchema';
import { clearBootstrap, loadBootstrap } from '../../redux/slices/bootstrap';
import { useBootstrapSelector } from '../../redux/selectorHooks';
import { DimmerLoader } from '../Loaders';
import { loadIncidentTypesAction } from '../../redux/actions/incidents';
import {
  defaultQuickAccessDictionaryState,
  initialUserSettingsSliceState,
  IUserSettings,
  populateUserSettings,
  SidebarState,
} from '../../redux/slices/userSettings';
import { globalFormat } from '../../utils/formatting';
import { dashboardSidebarSettingSchema } from '../../pages/DashboardRoute/DashboardNav/components/validation';
import { useAppSelector } from '../../redux/hooks';
import useUser from '../../utils/hooks/useUser';
import { useAnalytics } from '../../utils/analytics/useAnalytics';
import {
  isDefaultViewMember,
  isSidebarViewStateMember,
} from '../../utils/userSettings';
import { produce } from 'immer';
import useDarkModeEffect from './useDarkModeEffect';
import {
  MAX_SCRATCHPAD_WIDTH,
  MIN_SCRATCHPAD_WIDTH,
} from '../../config/constants';
import { toCustomerPlan } from '../../redux/slices/bootstrap/utils';

interface IProps {
  children: ReactNode;
}

/**
 * This component bootstraps global app settings that can affect its view.
 * all fields under bootstrap should be loaded only once,
 * and not allowed to change during the app lifecycle
 * */
export default ({ children }: IProps) => {
  const dispatch = useDispatch();
  const { roles } = useAppSelector((state) => state.permission);
  const user = useUser();
  const { init: initAnalytics } = useAnalytics();

  const bootstrapLoaded = useBootstrapSelector('bootstrapLoaded');

  const getUserSettings = (data: IBootstrapQueryQuery): IUserSettings => {
    const {
      jupyterHostSetting,
      quickAccessDictionarySetting,
      dashboardSidebarSetting,
      executionHistoryExpandedSetting,
      defaultViewSetting,
      schemaConfig,
      defaultSchemaSetting,
      sidebarPinnedSetting,
      sidebarSizeSetting,
      defaultColorSchemaSetting,
    } = data;

    return produce(initialUserSettingsSliceState, (draft) => {
      // Set jupyter host
      draft.jupyterHost = jupyterHostSetting?.value ?? '';

      // Set execution history expanded state
      if (executionHistoryExpandedSetting?.value) {
        draft.isExecutionHistorySidebarExpanded =
          executionHistoryExpandedSetting.value === 'true';
      }

      // Set quick access dictionary state
      if (
        quickAccessDictionarySetting?.value &&
        isSidebarViewStateMember(quickAccessDictionarySetting.value)
      ) {
        draft.sidebarState.quickAccessDictionary = {
          ...defaultQuickAccessDictionaryState,
          viewState: quickAccessDictionarySetting.value,
        };
      }

      // Set dashboard sidebar settings
      if (dashboardSidebarSetting?.value) {
        try {
          const parsedSettings = JSON.parse(dashboardSidebarSetting.value);
          draft.dashboardSidebarSetting =
            dashboardSidebarSettingSchema.parse(parsedSettings);
        } catch (err) {
          console.error(
            `Error parsing dashboard sidebar settings: ${dashboardSidebarSetting.value}`,
            err,
          );
        }
      }

      // Set sidebar pinned state
      if (sidebarPinnedSetting?.value) {
        draft.sidebarState.sidebarState = SidebarState.Collapsed;

        if (sidebarPinnedSetting.value === SidebarState.Dictionary) {
          draft.sidebarState.sidebarState = SidebarState.Dictionary;
        } else if (sidebarPinnedSetting.value === SidebarState.Scratchpad) {
          draft.sidebarState.sidebarState = SidebarState.Scratchpad;
        } else if (sidebarPinnedSetting.value === SidebarState.ExplainWithAi) {
          draft.sidebarState.sidebarState = SidebarState.ExplainWithAi;
        }
      }

      // Set default view
      if (
        defaultViewSetting?.value &&
        isDefaultViewMember(defaultViewSetting.value)
      ) {
        draft.defaultView = defaultViewSetting.value;
      }

      // Set default schema
      if (defaultSchemaSetting?.value) {
        draft.defaultSchemaId = defaultSchemaSetting.value;
      } else if (schemaConfig?.defaultSchemaId) {
        draft.defaultSchemaId = schemaConfig.defaultSchemaId;
      }

      // Set sidebar size
      if (sidebarSizeSetting?.value) {
        const parsedValue = parseInt(sidebarSizeSetting.value, 10);

        // KBT-1514 reset scratchpad size if it exceeds limits
        draft.sidebarSize =
          parsedValue <= MAX_SCRATCHPAD_WIDTH
            ? parsedValue
            : MIN_SCRATCHPAD_WIDTH;
      }

      // Set color schema
      if (defaultColorSchemaSetting?.value) {
        draft.colorSchemaSetting = defaultColorSchemaSetting.value;
      }
    });
  };

  const onCompleted = (data: IBootstrapQueryQuery) => {
    const {
      enabledFeatures,
      slackSupportLink,
      timeUnits,
      defaultEnd,
      timezone,
      preventBasicEventMetadataUpdates,
      formatSetting,
      reportSetting,
      llmApiEnabled,
    } = data.globalConfig;
    const { incidentTypes, schemaConfig, permissionConfig } = data;

    const userSettings = getUserSettings(data);
    const customerPlan = toCustomerPlan(data.customerPlan);
    const customerName = data.customerInfo.name;

    batch(() => {
      dispatch(populateUserSettings(userSettings));

      const defaultConfig = { timeUnits, defaultEnd, timezone };
      dispatch(
        loadBootstrap({
          enabledFeatures,
          slackSupportLink,
          defaultConfig,
          bootstrapLoaded: true,
          scheduleEmailDomains: data.scheduleConfig?.domains ?? [],
          schemaConfig,
          permissionConfig,
          preventBasicEventMetadataUpdates,
          formatSetting,
          reportSetting,
          llmApiEnabled,
          customerPlan,
          customerName,
        }),
      );
      dispatch(loadIncidentTypesAction(incidentTypes ?? []));
    });
    globalFormat.updateFormatConfig(formatSetting);
    initAnalytics(data.customerInfo, user, roles);
  };

  // GraphQL query "bootstrapQuery" pulls several settings at the time. We can't
  // interpolate UserSettingKind values in the query because GraphQL codegen will
  // not produce the IBootstrapQueryQuery type because the query is no longer
  // static string. Instead of hardcoding these values the query, we better pass
  // them as variable (albeit they are constants)
  useQuery<IBootstrapQueryQuery, IBootstrapQueryQueryVariables>(
    bootstrapQuery,
    {
      variables: {
        dashboardSidebarKind: UserSettingKind.DashboardSidebar,
        executionHistoryExpandedKind: UserSettingKind.ExecutionHistoryExpanded,
        jupiterHostKind: UserSettingKind.JupyterHost,
        quickAccessDictionaryKind: UserSettingKind.QuickAccessDictionary,
        defaultViewKind: UserSettingKind.DefaultView,
        defaultSchemaKind: UserSettingKind.DefaultSchema,
        sidebarPinnedKind: UserSettingKind.SidebarPinned,
        sidebarSizeKind: UserSettingKind.SidebarSize,
        colorSchemaKind: UserSettingKind.ColorSchema,
      },
      onCompleted,
    },
  );

  useDarkModeEffect();

  // Clear bootstrap state when component unmounts, for example on logout
  useEffect(() => {
    return () => {
      dispatch(clearBootstrap());
    };
  }, [dispatch]);

  // we can't use `{ loading }` from `useQuery` because there is race condition between loading = false and
  // populating the store

  if (!bootstrapLoaded) {
    return <DimmerLoader />;
  }

  return <>{children}</>;
};
